import { commonWeakPasswordCores } from "./RHCommonWeakPasswordCores"

// Imported directly by the backend too (controllers/UserController.ts) - keep this file free of uicore-ts imports.
export class RHUserPasswordPolicy {
    static minimumPasswordLength = 15
    static maximumPasswordLength = 128

    // NIST 800-63B / OWASP ASVS guidance: reject predictable passwords instead of
    // demanding character-class complexity (which mostly produces "Passw0rd!").
    // Backed by a bundled denylist of the top 3,500 real-world common passwords
    // (RHCommonWeakPasswordCores.ts) rather than a short hand-picked list, loaded
    // into a Set for O(1) lookup.
    static readonly _commonWeakPasswordCores = new Set(commonWeakPasswordCores)

    static readonly _keyboardRuns = [
        "qwertyuiop", "asdfghjkl", "zxcvbnm", "1234567890"
    ]

    static passwordMeetsMinimumLength(password: string | null | undefined) {
        return typeof password === "string" && password.length >= RHUserPasswordPolicy.minimumPasswordLength
    }

    static passwordMeetsMaximumLength(password: string | null | undefined) {
        return typeof password === "string" && password.length <= RHUserPasswordPolicy.maximumPasswordLength
    }

    static passwordAvoidsCommonPatterns(password: string | null | undefined) {

        let normalized = ""
        if (typeof password === "string") {
            normalized = password.toLowerCase()
        }

        const lettersOnly = normalized.replace(/[^a-z]/g, "")

        return normalized.length > 0 &&
            !RHUserPasswordPolicy._commonWeakPasswordCores.has(lettersOnly) &&
            !RHUserPasswordPolicy._hasNegligibleCharacterVariety(normalized) &&
            !RHUserPasswordPolicy._isTiledFromAShortRepeatingUnit(normalized) &&
            !RHUserPasswordPolicy._containsLongSequentialRun(normalized)

    }

    static minimumLengthRequirementMessage() {
        return `Parool peab olema vähemalt ${RHUserPasswordPolicy.minimumPasswordLength} tähemärki pikk`
    }

    static maximumLengthRequirementMessage() {
        return `Parool tohib olla kuni ${RHUserPasswordPolicy.maximumPasswordLength} tähemärki pikk`
    }

    static commonPatternRequirementMessage() {
        return "Parool on liiga lihtsalt äraarvatav (nt korduvad märgid, järjestikused numbrid/tähed või levinud sõna). Palun vali unikaalsem parool."
    }

    static _hasNegligibleCharacterVariety(normalizedPassword: string) {
        return new Set(normalizedPassword).size <= 3
    }

    static _isTiledFromAShortRepeatingUnit(normalizedPassword: string) {

        const length = normalizedPassword.length
        const candidateUnitLengths = Array.from({ length: Math.floor(length / 2) }, (_, index) => index + 1)

        return candidateUnitLengths.some((unitLength) => {
            const unit = normalizedPassword.slice(0, unitLength)
            const numberOfRepeats = Math.ceil(length / unitLength)
            return unit.repeat(numberOfRepeats).slice(0, length) === normalizedPassword
        })

    }

    static _containsLongSequentialRun(normalizedPassword: string, minimumRunLength = 6) {

        let ascendingRunLength = 1
        let descendingRunLength = 1

        for (let index = 1; index < normalizedPassword.length; index++) {

            const delta = normalizedPassword.charCodeAt(index) - normalizedPassword.charCodeAt(index - 1)

            if (delta === 1) {
                ascendingRunLength += 1
            }
            else {
                ascendingRunLength = 1
            }

            if (delta === -1) {
                descendingRunLength += 1
            }
            else {
                descendingRunLength = 1
            }

            const runIsLongEnough = ascendingRunLength >= minimumRunLength || descendingRunLength >= minimumRunLength
            if (runIsLongEnough) {
                return runIsLongEnough
            }

        }

        const keyboardRunSegments = RHUserPasswordPolicy._keyboardRuns.flatMap((row) => {
            const reversedRow = row.split("").reverse().join("")
            const segments: string[] = []
            for (let start = 0; start + minimumRunLength <= row.length; start++) {
                segments.push(row.slice(start, start + minimumRunLength))
                segments.push(reversedRow.slice(start, start + minimumRunLength))
            }
            return segments
        })

        return keyboardRunSegments.some((segment) => normalizedPassword.includes(segment))

    }
}
